import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { fmt2, isInternal } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { TrendingUp } from "lucide-react";

const BUCKET_COLORS = { "0-30": "#059669", "31-60": "#d97706", "61-90": "#ea580c", ">90": "#e11d48" };

export default function Receivables({ embedded = false }) {
  const { user, selectedClientId, clients } = useAuth();
  const { t } = useI18n();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;
  const client = clients.find((c) => c.id === clientId);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) { setData(null); return; }
    const { data } = await api.get(`/reports/receivables-aging?client_id=${clientId}`);
    setData(data);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const buckets = data?.buckets || [];
  const chartData = buckets.map((b) => ({ name: b.bucket, amount: b.amount }));

  return (
    <div className="space-y-4" data-testid="receivables-page">
      {!embedded && (
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" /> {t("rcv.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("rcv.desc")} {client && `· ${client.name}`}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-4 md:col-span-1 col-span-2 bg-primary text-primary-foreground">
          <p className="text-xs opacity-80">{t("rcv.totalOutstanding")}</p>
          <p className="num text-xl font-bold mt-1" data-testid="rcv-total">{fmt2(data?.total_outstanding || 0)}</p>
        </Card>
        {buckets.map((b) => (
          <Card key={b.bucket} className="p-4" data-testid={`rcv-bucket-${b.bucket}`}>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: BUCKET_COLORS[b.bucket] }} />{b.bucket} {t("rcv.daysOverdue").toLowerCase()}
            </p>
            <p className="num text-lg font-semibold mt-1">{fmt2(b.amount)}</p>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <h2 className="font-head font-semibold mb-3">{t("rcv.bucket")}</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis fontSize={11} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v)} />
              <Tooltip formatter={(v) => fmt2(v)} />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={BUCKET_COLORS[e.name] || "#2563eb"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4">
        <div className="overflow-auto rounded-md border border-border">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead>{t("rcv.invoice")}</TableHead><TableHead>{t("rcv.customer")}</TableHead>
              <TableHead>{t("rcv.dueDate")}</TableHead><TableHead className="text-right">{t("rcv.daysOverdue")}</TableHead>
              <TableHead className="text-right">{t("rcv.balance")}</TableHead><TableHead>{t("rcv.bucket")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(!data || data.rows.length === 0) && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">{t("rcv.noData")}</TableCell></TableRow>}
              {(data?.rows || []).map((r) => (
                <TableRow key={r.id} data-testid={`rcv-row-${r.id}`}>
                  <TableCell className="text-xs font-medium">{r.invoice_no}</TableCell>
                  <TableCell className="text-sm">{r.customer || "—"}</TableCell>
                  <TableCell className="num text-xs">{fmtDate(r.due_date || r.date)}</TableCell>
                  <TableCell className="num text-right text-xs">{r.days_overdue}</TableCell>
                  <TableCell className="num text-right text-xs font-semibold">{fmt2(r.balance_due)}</TableCell>
                  <TableCell><Badge className="text-[10px]" style={{ background: `${BUCKET_COLORS[r.bucket]}22`, color: BUCKET_COLORS[r.bucket] }}>{r.bucket}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
